import { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from 'primereact/skeleton';
import { FaChevronDown, FaChevronUp, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import handbooksImg from '../../../assets/icons/HandbookIcon.svg';
import presentationsImg from '../../../assets/icons/PresentationIcon.svg';
import installConfigImg from '../../../assets/icons/InstallGuideIcon.svg';
import knowledgeDocImg from '../../../assets/icons/DocumentIcon.svg';
import faqCyberImg from '../../../assets/icons/FaqIcon.svg';
import AuthContext from '../../../auth/AuthContext';
import AlertService from '../../../services/AlertService';
import View from './View';
import DocNameCard from './DocNameCard';
import DocumentService from '../../../services/DocumentService';
import AddDocumentModal from './AddDocumentModal';
import ConfirmDialogBox from '../../confirmDialog/ConfirmDialogBox';
import DocumentTypeService from '../../../services/DocumentTypeService';
import { ThemeContext } from '../../../theme/ThemeContext';

if (!Object.groupBy) {
  Object.groupBy = (array, keyFn) =>
    array.reduce((result, item) => {
      const key = keyFn(item);
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, {});
}

const DocDetails = () => {
  const { docTypeId } = useParams();
  const { state } = useLocation();
  const authContext = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [groupedDocuments, setGroupedDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false); 
  const [documentsData, setDocumentsData] = useState([]);
  const [tags, setTags] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editDocument, setEditDocument] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [activeTag, setActiveTag] = useState("All");
  const navigate = useNavigate();
  const cardAbortRef = useRef(null); 
  const activeDocumentRef = useRef(null);

   useEffect(() => {
  if (state?.tag) {
    setActiveTag(state.tag);
  }
}, [state]);

  // Permissions
useEffect(() => {
  const documentAccess = authContext.getPermissions("documents") || [];

  if (!documentAccess.some(f => f.access.includes(1)) || authContext.onGoingValidations) {
    navigate("/");
  }

  setShowAdd(documentAccess.some(f => f.access.includes(2)));
  setShowEdit(documentAccess.some(f => f.access.includes(3)));
  setShowDelete(documentAccess.some(f => f.access.includes(4)));

}, [navigate, authContext]);

  const isAllCategory = !docTypeId || docTypeId === 'documents';

  const groupDocuments = useCallback((documents) => {
    const parentGroups = Object.groupBy(documents, d => d.taggedUnderDesc || '');
    return Object.entries(parentGroups).map(([tag, items]) => {
      const childGroups = Object.groupBy(items, d => d.documentType || '');
      const children = Object.entries(childGroups).map(([docType, docs]) => ({
        key: docType,
        visible: true,
        items: docs,
      }));

      return {
        key: tag,
        items: children,
      };
    });
  }, []);

  // Memoized filtered grouped documents
  const filteredGroupedDocuments = useMemo(() => {
    if (!groupedDocuments) return [];
    if (activeTag === "All") return groupedDocuments;
    return groupedDocuments.filter(group => group.key === activeTag);
  }, [groupedDocuments, activeTag]);

  useEffect(() => {
    if (activeDocumentRef.current) {
      activeDocumentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [selectedDocument]);

  const toggleChildVisibility = (parentKey, childKey) => {
    const updated = groupedDocuments.map(parent => {
      if (parent.key !== parentKey) return parent;
      return {
        ...parent,
        items: parent.items.map(child => {
          if (child.key !== childKey) return child;
          return { ...child, visible: !child.visible };
        }),
      };
    });
    setGroupedDocuments(updated);
  };

const loadDocument = useCallback(
    async (id, forceFetch = false, signal) => { 
      setViewLoading(true);
      let doc = null;
      if (!forceFetch) {
        doc = documentsData.find(d => d.id === id);
      }
      if (!doc || forceFetch) {
        try {
          const response = await DocumentService.Get(id, authContext, signal); 

          if (signal?.aborted) return; 

          if (response.isSuccess) {
            setDocumentsData(prev => prev.find(d => d.id === response.data.id) ? prev : [...prev, response.data]);
            setSelectedDocument(response.data);
          } else {
            AlertService.error(response.errorMessage);
          }
        } catch (err) {
          if (err.name === "AbortError") return; 
        }
      } else {
        setSelectedDocument(doc);
      }
      if (!signal?.aborted) setViewLoading(false); 
    },
    [authContext, documentsData]
  );


const loadDocuments = useCallback(async (signal) => { 
    setLoading(true);
    try {
      let data = await DocumentService.GetAll('', authContext, signal);

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setGroupedDocuments(groupDocuments(data.data));
        setDocumentsData(data.data);
      }
      if (data && !data.isSuccess) {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to load documents.");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [authContext, groupDocuments]);

useEffect(() => {
    const controller = new AbortController(); 
    loadDocuments(controller.signal);
    return () => controller.abort(); 
  }, [loadDocuments]);

const loadTags = useCallback(async (signal) => { 
    setLoading(true);
    try {
      let data = await DocumentTypeService.GetDocumentTags(authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setTags(data.data);
      }
      if (data && !data.isSuccess) {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return; 
      AlertService.error("Failed to load documents.");
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController();
    loadTags(controller.signal);
    return () => controller.abort(); 
  }, [loadTags]);



const handleCardClick = (doc) => {
    setSelectedDocument(doc);
    if (doc?.id) {
      if (cardAbortRef.current) {
        cardAbortRef.current.abort(); 
      }
      const controller = new AbortController();
      cardAbortRef.current = controller;
      loadDocument(doc.id, true, controller.signal); 
    }
  };

  const handleShowDeleteDialog = (doc) => {
    setSelectedDocument(doc);
    setTimeout(() => setShowDeleteConfirm(true), 200);
  };

  const onDeleteHandler = async () => {
    setShowDeleteConfirm(false);
    const response = await DocumentService.Remove(selectedDocument.id, authContext);

    if (response.isSuccess) {
      AlertService.success("Document deleted successfully!");
      setSelectedDocument(null);
      await loadDocuments();
    } else {
      AlertService.error(response.errorMessage);
    }
  };

  const handleAfterDocumentSave = async (updatedDoc) => {
    await loadDocuments();
    if (updatedDoc?.id) {
      handleCardClick({ id: updatedDoc.id });
    } else {
      setSelectedDocument(null);
    }
  };

  const filterCards = [
    { label: "HandBooks", imgSrc: handbooksImg },
    { label: "Presentations", imgSrc: presentationsImg },
    { label: "Install & Configuration Guide", imgSrc: installConfigImg },
    { label: "Knowledge Document", imgSrc: knowledgeDocImg },
    { label: "FAQ-Cyber Range", imgSrc: faqCyberImg },
  ];

  return (
    <div className="container-fluid">
      {!selectedDocument && (
        <div className="d-flex justify-content-end mt-1 gap-3">
          {(showAdd || (isAllCategory && showAdd)) && (
            <button className="btn btn-primary py-2" onClick={() => { setEditDocument(null); setShowModal(true); }}>
              <FaPlus className="pb-1 me-1" />
              Add New Document
            </button>
          )}
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-start justify-content-xl-around gap-3 pt-3">
        {loading ? (
          [...Array(8)].map((_, idx) => (
            <div key={idx} style={{ minWidth: 150, minHeight: 70 }}>
              <Skeleton width={150} height={70} shape="rounded" />
            </div>
          ))
        ) : (
          ["All", ...tags.map(m => m.label)].map(tag => {
            const card = filterCards.find(c => c.label === tag);
            return (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className={`filter-card d-flex flex-row align-items-center border-0 justify-content-center shadow py-3 px-4 ${activeTag === tag ? 'active' : ''}`}
                style={{
                  borderRadius: 12,
                  cursor: "pointer",
                  backgroundColor: isDark ? "#191919" : "#fff",
                  minWidth: 80,
                  minHeight: 40,
                  outline: "none",
                  position: "relative",
                  gap: "10px"
                }}
              >
                {tag !== "All" && card ? (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    overflow: "hidden"
                  }}>
                    <img src={card.imgSrc} alt={tag} style={{ width: 40, height: 40, objectFit: "contain" }} />
                  </span>
                ) : null}
                <span className={activeTag === tag ? "text-active" : "text-primary"}
                  style={{
                    fontWeight: activeTag === tag ? 600 : 500,
                    fontSize: 16,
                    color: activeTag === tag ? "#3182ce" : "",
                    flex: tag === "All" ? 1 : undefined,
                    textAlign: tag === "All" ? "center" : undefined
                  }}>
                  {tag}
                </span>
              </button>
            );
          })
        )}
      </div>

      {!selectedDocument ? (
        loading ? (
          <div className="row mt-4">
            {[...Array(8)].map((_, idx) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-4" key={idx}>
                <div className="shadow-sm border-0 rounded-3 p-3" style={{ minHeight: '120px' }}>
                  <Skeleton width="60%" height="1.5rem" className="mb-2" />
                  <Skeleton width="30%" height="1rem" className="mb-2" />
                  <Skeleton width="80%" height="1.25rem" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredGroupedDocuments.length > 0 ? (
          filteredGroupedDocuments.map(parentGroup => (
            <div key={parentGroup.key} className="mb-5 mt-4">
              <h5 className="text-start text-primary mb-3 ms-3">{parentGroup.key || ''}</h5>

              {parentGroup.items.map(childGroup => {
                if (childGroup.items.length === 0) return null;

                return (
                  <div key={childGroup.key} className="mb-3">
                    <div
                      className={`position-relative p-3 mb-3 rounded cursor-pointer ${isDark
                        ? "bg-dark border border-dark text-light"
                        : "bg-light border text-dark"
                      }`}
                      onClick={() => toggleChildVisibility(parentGroup.key, childGroup.key)}
                    >
                      <div className="d-flex justify-content-start align-items-center">
                        <span className="position-absolute">{childGroup.key || 'No Subcategory'}</span>
                        <div
                          className={`ms-auto d-flex align-items-center justify-content-center rounded-circle border ${isDark ? "bg-dark border-light text-white" : "bg-white border text-dark"}`}
                          style={{ width: "28px", height: "28px" }}
                        >
                          {childGroup.visible ? (
                            <FaChevronUp className="fs-6" />
                          ) : (
                            <FaChevronDown className="fs-6" />
                          )}
                        </div>
                      </div>
                    </div>

                    {childGroup.visible && (
                      <div className="row">
                        {childGroup.items.map((document, ix) => (
                          <div
                            key={document.id}
                            className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3"
                            onClick={() => handleCardClick(document)}
                            style={{ cursor: 'pointer' }}
                          >
                            <DocNameCard
                              document={document}
                              no={ix + 1}
                              isActive={selectedDocument?.id === document.id}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <div className="text-center py-5 text-muted">No Documents found</div>
        )
      ) : (
        <div className="row g-2 mt-1">
          <div className="d-flex justify-content-end gap-2">
            {showDelete && (
              <button className="btn text-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
                <FaTrash className="me-1 fs-5 mb-1" /> Delete
              </button>
            )}
            {showEdit && (
              <button
                className="btn text-primary btn-sm"
                onClick={() => { setEditDocument(selectedDocument); setShowModal(true); }}
              >
                <FaEdit className="me-1 fs-5 mb-1" /> Edit
              </button>
            )}
          </div>

          <div className="col-md-3 col-lg-2">
            <div className="pe-2 scroll-no-buttons" style={{ minHeight: "73vh", maxHeight: "73vh", overflowY: "auto", overflowX: "hidden" }}>
              {filteredGroupedDocuments.map(parentGroup => (
                <div key={parentGroup.key}>
                  <h5 className="text-center mb-2" style={{ color: isDark?'#3182ce':'#003366' }}>{parentGroup.key || ''}</h5>
                  {parentGroup.items.map(childGroup => {
                    if (childGroup.items.length === 0) return null;

                    return (
                      <div key={childGroup.key}>
                        <h6 className="text-center fw-semibold mb-2">{childGroup.key || ''}</h6>
                        {childGroup.items.map((document, ix) => {
                          const isActive = selectedDocument?.id === document.id;
                          return (
                            <div key={document.id} ref={isActive ? activeDocumentRef : null}>
                              <DocNameCard
                                document={document}
                                no={ix + 1}
                                clickHandler={handleCardClick}
                                isActive={isActive}
                                compact
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="col-12 col-md-10">
            {viewLoading ? (
              <div
                    style={{
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        maxHeight: "72vh",

                    }}
                >
                    <div style={{ background: "#f4f4f4", padding: "0.5rem", display: "flex", gap: "0.5rem" }}>
                        <Skeleton width="2rem" height="2rem" borderRadius="4px" />
                        <Skeleton width="2rem" height="2rem" borderRadius="4px" />
                        <Skeleton width="3rem" height="2rem" borderRadius="4px" />
                        <Skeleton width="4rem" height="2rem" borderRadius="4px" />
                        <Skeleton width="2rem" height="2rem" borderRadius="4px" style={{ marginLeft: "auto" }} />
                    </div>
                    <div style={{ padding: "1rem" }}>
                        <Skeleton width="100%" height="700px" borderRadius="6px" />
                    </div>
                </div>
            ) : (
              <View document={selectedDocument} />
            )}
          </div>
        </div>
      )}

      <AddDocumentModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        document={editDocument}
        openParentDeleteDialog={handleShowDeleteDialog}
        reloadList={loadDocuments}
        onSaved={handleAfterDocumentSave}
        documentTypeId={state?.value || docTypeId}
      />

      <ConfirmDialogBox
        visible={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={onDeleteHandler}
        title={<>Are you sure you want to delete this Document?</>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default DocDetails;